import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Save, Trash2, Loader2, ChevronDown, ChevronRight, AlertTriangle, Check } from 'lucide-react';
import ImageUploader from './ImageUploader';
import StaffAssignmentsEditor from './StaffAssignmentsEditor';
import axios from 'axios';
import bridgeApi from './bridgeApi';

// ── Naming Conventions slug sanitizer (per NAMING_CONVENTIONS.md Section 3) ──
function sanitizeSlug(raw) {
  return (raw || '').toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
}

// ── Real-opponent picker filters (Games → Opponent) ──
// The `teams` collection mixes real opponents with (a) the Sabres' OWN teams —
// the 14 canonical rows (code + sport_id), the sport-prefixed mirror rows
// ("Basketball Varsity Girls"), and the bare level/gender names ("Varsity
// Girls") — and (b) non-competition "event" rows ("3 Point Contest", "All
// Sabres Teams", banquets, rallies…). A field with `optionFilter: 'opponents'`
// hides both so the dropdown lists only genuine opponents.
// Mirror of backend `_norm_ident`: lowercase, collapse non-alphanumerics to a
// single space, trim — for EXACT identity comparison against /own-identity.
function normIdent(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}
const EVENT_ROW_RX = /\b(contest|banquet|rally|try[\s-]?outs?|pep|clinic|ceremony|awards?|senior night|media day|photo day|jamboree|invitational|tournament|alumni|all sabres|showcase|festival|fundraiser|cookout|picnic|orientation|registration|meeting)\b/i;

function isRealOpponent(e, labelOf, ownIdentity) {
  if (e.code && e.sport_id) return false;          // canonical Sabres team
  const label = (labelOf(e) || '').trim();
  if (ownIdentity && ownIdentity.has(normIdent(label))) return false;  // Sabres own team (any naming), DB-derived
  if (EVENT_ROW_RX.test(label)) return false;      // event / non-team row
  return true;
}

// ── Games → "Select Schedule" picker (field type `schedule_picker`) ──
// Default to current-season schedules only, with a checkbox to reveal all
// past seasons. Labels never show the raw slug. Extracted as a component
// because the field renderer uses early returns (can't add hooks inline).
function _schedYear(s) {
  const src = `${s.slug || ''} ${s.title || s.name || ''}`;
  const years = (src.match(/\b(19|20)\d{2}\b/g) || []).map(Number);
  return years.length ? Math.max(...years) : null;
}
function _schedSportKey(s) {
  const slug = s.slug || '';
  return slug.includes('-') ? slug.split('-')[0] : String(s.sport_id || 'other');
}
function _schedLabel(s) {
  let t = (s.title || s.name || '').trim();
  if (s.slug) t = t.replace(` (${s.slug})`, '').trim(); // never show the slug
  return t || s.slug || 'Untitled';
}

function SchedulePicker({ entities, v, onChange }) {
  const [currentOnly, setCurrentOnly] = useState(true);
  const [sportFilter, setSportFilter] = useState(''); // '' = all sports
  const schedList = entities?.schedules || [];

  // Sport options come from the org's ACTIVE sports (SOT — same as the Sports
  // tab), value = the 2-char code (schedule slug prefix). Never hardcoded.
  const sportOpts = [];
  const seenSport = new Set();
  for (const s of (entities?.sports || [])) {
    const code = s.slug || String(s.id);
    if (!code || seenSport.has(code)) continue;
    seenSport.add(code);
    sportOpts.push({ code, label: s.title || s.name || code });
  }
  sportOpts.sort((a, b) => a.label.localeCompare(b.label));

  const seen = new Set();
  const uniqueSched = [];
  for (const s of schedList) {
    const key = s.slug || String(s.id);
    if (seen.has(key)) continue;
    seen.add(key);
    uniqueSched.push(s);
  }

  const maxYearBySport = {};
  for (const s of uniqueSched) {
    const y = _schedYear(s);
    if (y == null) continue;
    const k = _schedSportKey(s);
    if (maxYearBySport[k] == null || y > maxYearBySport[k]) maxYearBySport[k] = y;
  }
  const isCurrent = (s) => {
    const y = _schedYear(s);
    if (y == null) return true; // never hide an undated real row
    return y === maxYearBySport[_schedSportKey(s)];
  };

  let visible = currentOnly ? uniqueSched.filter(isCurrent) : uniqueSched;
  if (sportFilter) visible = visible.filter(s => _schedSportKey(s) === sportFilter);
  // Always keep the currently-selected schedule visible, even if it's an old season.
  if (v && !visible.some(s => (s.slug || String(s.id)) === v)) {
    const cur = uniqueSched.find(s => (s.slug || String(s.id)) === v);
    if (cur) visible = [cur, ...visible];
  }
  visible = visible.slice().sort((a, b) => _schedLabel(a).localeCompare(_schedLabel(b)));

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer w-fit">
          <input type="checkbox" checked={currentOnly}
            onChange={e => setCurrentOnly(e.target.checked)}
            className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
            data-testid="schedule-current-season-only" />
          <span className="text-xs text-slate-300">Current Season only</span>
        </label>
        <Select value={sportFilter || '__all__'} onValueChange={nv => setSportFilter(nv === '__all__' ? '' : nv)}>
          <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-8 w-40" data-testid="schedule-filter-sport">
            <SelectValue placeholder="All sports" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All sports</SelectItem>
            {sportOpts.map(o => (
              <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Select value={String(v || '__none__')} onValueChange={nv => onChange(nv === '__none__' ? '' : nv)}>
        <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid="schedule-picker-trigger">
          <SelectValue placeholder="Select schedule" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">None</SelectItem>
          {visible.map(s => (
            <SelectItem key={s.slug || s.id} value={s.slug || String(s.id)}>{_schedLabel(s)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}


// ── Season (roster context) selector for the Players pencil form ──────────
// Picks WHICH season roster a Team/Level assignment applies to. Season lives
// on the ROSTER, never on the player. Defaults to the player's CURRENT
// membership season if it has one (so a plain edit doesn't silently move the
// player), else the current season. Options = the same seasons the grid uses.
function SeasonRosterSelect({ entities, v, onChange, allValues }) {
  const seasons = entities?.seasons || [];
  const slugSeason = (slug) => { const m = /(20\d\d)/.exec(slug || ''); return m ? `${m[1]}-${Number(m[1]) + 1}` : ''; };
  const def = React.useMemo(() => {
    const mem = slugSeason(allValues?.current_team_slug || (allValues?.team_slugs || [])[0]);
    if (mem && seasons.some(s => (s.slug || s.id) === mem)) return mem;
    const cur = seasons.find(s => s.is_current) || seasons.find(s => (s.count || 0) > 0) || seasons[0];
    return cur ? (cur.slug || cur.id) : '';
  }, [seasons, allValues]);
  useEffect(() => { if (!v && def) onChange(def); }, [def]);  // seed default once
  return (
    <select data-testid="player-roster-season" value={v || def || ''}
      onChange={e => onChange(e.target.value)}
      className="bg-slate-900 border border-slate-700 text-slate-200 h-9 rounded px-2 w-full text-sm">
      {seasons.map(s => <option key={s.slug || s.id} value={s.slug || s.id}>{s.title || s.name || s.slug}</option>)}
    </select>
  );
}


// ── Slug generator per entity type (per NAMING_CONVENTIONS.md Section 5-6) ──
function generateSlug(entityKey, values, entities) {
  const sport = (entities?.sports || []).find(s => String(s.id) === String(values.sport_id));
  const sportSlug = sport?.slug || '';

  if (entityKey === 'schedules') {
    // Slug is built server-side (canonical {sport}-{lg}-{year}[-term]); the form
    // hides the Slug field. See WP_SCHEDULES + WP_MGMT_TAB_RELATIONSHIPS.
    return '';
  }
  if (entityKey === 'teams') {
    // Format: {sport}-{level}-{gender} (no year — team identity is year-agnostic)
    const parts = [];
    if (sportSlug) parts.push(sportSlug);
    if (values.level) parts.push(values.level);
    if (values.gender) parts.push(values.gender);
    return sanitizeSlug(parts.join('-'));
  }
  if (entityKey === 'seasons') {
    // Format: {year}-{season}
    const parts = [];
    if (values.year) parts.push(String(values.year));
    if (values.season_type) parts.push(values.season_type);
    return sanitizeSlug(parts.join('-'));
  }
  if (entityKey === 'staff-groups') {
    // Canonical: team → {sport}-{lg}-staff ; org → org-{area}-staff
    const scope = values.scope || ((values.sport && values.level_gender) ? 'team' : (values.area ? 'org' : 'team'));
    if (scope === 'org') return sanitizeSlug(`org-${values.area || ''}-staff`);
    const map = { basketball: 'bk', baseball: 'bb', volleyball: 'vl', soccer: 'sc', football: 'fb' };
    const sp = map[values.sport] || values.sport || '';
    const lg = values.level_gender || '';
    if (sp && lg) return sanitizeSlug(`${sp}-${lg}-staff`);
    return sanitizeSlug(values.title || values.name || '');
  }
  // Default: sanitize title
  return sanitizeSlug(values.title || values.name || '');
}

function RosterPicker({ entities, v, onChange, onNavigate, allValues }) {
  const rosters = entities?.rosters || [];
  // Academic season per roster comes from the backend union (`r.season`, e.g.
  // "2026-2027"). Show the CURRENT academic season + any FUTURE ones — so all
  // three sports' current rosters appear together (baseball's spring-year slug
  // is already folded into the right academic year server-side).
  const seasonOf = (r) => r.season || '';
  const curSeason = rosters.reduce((mx, r) => { const s = seasonOf(r); return s > mx ? s : mx; }, '');
  let opts = rosters
    .filter(r => r.slug && seasonOf(r) && seasonOf(r) >= curSeason)
    .map(r => ({ v: r.slug, l: r.name || r.slug, s: seasonOf(r) }))
    .sort((a, b) => a.l.localeCompare(b.l));
  const multiSeason = new Set(opts.map(o => o.s)).size > 1;
  // Preselect the player's current roster on edit; keep it selectable even if
  // it's a prior-season roster not in the current+future option list.
  const preset = v || allValues?.current_team_slug || '';
  if (preset && !opts.some(o => o.v === preset)) {
    const cur = rosters.find(r => r.slug === preset);
    if (cur) opts = [{ v: cur.slug, l: cur.name || cur.slug, s: seasonOf(cur) }, ...opts];
  }
  return (
    <Select value={preset || '__none__'} onValueChange={nv => {
      if (nv === '__add_roster__') { onNavigate?.('rosters', 'new'); return; }
      onChange(nv === '__none__' ? '' : nv);
    }}>
      <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid="player-roster-picker">
        <SelectValue placeholder="Select a roster team…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">Select a roster team…</SelectItem>
        {opts.map(o => <SelectItem key={o.v} value={o.v}>{o.l}{multiSeason && o.s ? ` — ${o.s}` : ''}</SelectItem>)}
        <SelectItem value="__add_roster__" data-testid="player-roster-add-option">＋ Add Roster Team…</SelectItem>
      </SelectContent>
    </Select>
  );
}

function RosterComposer({ orgId, setField }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [opts, setOpts] = useState(null);
  const [sport, setSport] = useState('');
  const [level, setLevel] = useState('');
  const [year, setYear] = useState('');
  const [term, setTerm] = useState('');
  const [variant, setVariant] = useState('');
  const [preview, setPreview] = useState(null);
  const setFieldRef = useRef(setField);
  setFieldRef.current = setField;

  const loadOpts = React.useCallback((keepYear) => {
    fetch(`${BACKEND}/api/ol-canonical/roster-form-options/${orgId}`)
      .then(r => r.json()).then(d => {
        const o = d?.data || {}; setOpts(o);
        if (o.current_year && !keepYear) setYear(String(o.current_year));
      }).catch(() => setOpts({ sports: [], levels: [], terms: [], variants: [] }));
  }, [BACKEND, orgId]);
  useEffect(() => { loadOpts(false); }, [loadOpts]);

  // "+ Add Level…" / "+ Add New…" write the SAME org lists Org Teams ▸ Team Levels
  // uses (services/team_levels) — one vocabulary for both UIs (2026-10-13).
  const addToList = async (kind) => {
    const name = window.prompt(kind === 'levels' ? 'New level name (e.g. "Freshman") — it is added for Boys, Girls and Coed'
      : 'New variant (e.g. "Gold")');
    if (!name || !name.trim()) return null;
    try {
      const { data } = await axios.post(`${BACKEND}/api/ad-teams/${orgId}/${kind}`, { name: name.trim() });
      loadOpts(true);
      return data?.data;
    } catch (e) { window.alert(e?.response?.data?.detail || 'Could not add'); return null; }
  };

  useEffect(() => {
    const sf = setFieldRef.current;
    sf('sport', sport); sf('level', level); sf('year', year);
    sf('term', term); sf('variant', variant);
    if (!(sport && level && year)) {
      setPreview(null); sf('name', ''); sf('slug', ''); return;
    }
    const qs = new URLSearchParams({ sport, level, year, term, variant }).toString();
    let cancel = false;
    fetch(`${BACKEND}/api/ol-canonical/roster-compose/${orgId}?${qs}`)
      .then(r => r.json()).then(d => {
        if (cancel) return;
        const p = d?.data || null; setPreview(p);
        if (p && p.valid) { setFieldRef.current('name', p.name); setFieldRef.current('slug', p.slug); }
      }).catch(() => {});
    return () => { cancel = true; };
  }, [sport, level, year, term, variant, BACKEND, orgId]);

  // Year options come from the ONE season rule (/season-options: current + next
  // per sport), not a hardcoded cy..cy+4 span. Fetched when a sport is picked.
  const [seasonOpts, setSeasonOpts] = useState(null);
  useEffect(() => {
    if (!sport) { setSeasonOpts(null); return undefined; }
    let alive = true;
    fetch(`${BACKEND}/api/ol-canonical/season-options/${orgId}?sport=${encodeURIComponent(sport)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive) setSeasonOpts(Array.isArray(d?.data) ? d.data : null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [sport, BACKEND, orgId]);

  if (!opts) return <div className="text-sm text-slate-400">Loading roster options…</div>;
  const cy = Number(opts.current_year) || new Date().getFullYear();
  let years;
  if (seasonOpts && seasonOpts.length) {
    years = seasonOpts; // {value: year, label: "YYYY-YYYY"} from /season-options
  } else {
    years = [];
    for (let y = cy; y <= cy + 4; y++) years.push({ value: String(y), label: String(y) });
  }
  const Sel = (label, val, set, items, testid, placeholder) => (
    <div className="space-y-1">
      <label className="text-xs text-slate-400">{label}</label>
      <Select value={val || '__none__'} onValueChange={x => set(x === '__none__' ? '' : x)}>
        <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid={testid}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {placeholder ? <SelectItem value="__none__">{placeholder}</SelectItem> : null}
          {items.map(it => (
            <SelectItem key={it.value || '__none__'} value={it.value || '__none__'}>{it.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="roster-composer">
      <div className="grid grid-cols-2 gap-3">
        {Sel('Sport', sport, setSport, opts.sports || [], 'roster-sport', 'Select sport…')}
        {Sel('Level', level, async (x) => {
          if (x !== '__addlevel__') { setLevel(x); return; }
          await addToList('levels');      // new level × Boys/Girls/Coed appear in the list
        }, [...(opts.levels || []), { value: '__addlevel__', label: '+ Add Level…' }], 'roster-level', 'Select level…')}
        {Sel('School year', year, setYear, years, 'roster-year', 'Select year…')}
        {Sel('Season (optional)', term, setTerm, (opts.terms || []).map(t => (t.value ? t : { ...t, label: '—' })), 'roster-term', null)}
      </div>
      {/* Variant: pick an existing one OR add a new free-text variant */}
      <div className="space-y-1">
        <label className="text-xs text-slate-400">Variant (optional)</label>
        <Select value={variant || '__none__'}
          onValueChange={async x => {
            if (x !== '__new__') { setVariant(x === '__none__' ? '' : x); return; }
            const v = await addToList('variants');
            if (v) setVariant(String(v).toLowerCase());
          }}>
          <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid="roster-variant">
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {(opts.variants || []).filter(v => v.value).map(v => (
              <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
            ))}
            <SelectItem value="__new__">+ Add New…</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-md border border-slate-700 bg-slate-800/60 p-3" data-testid="roster-preview">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Will create</div>
        {preview && preview.valid ? (
          <div>
            <div className="text-sm text-slate-100" data-testid="roster-preview-name">{preview.name}</div>
            <div className="text-xs text-slate-400 font-mono" data-testid="roster-preview-slug">{preview.slug}</div>
            {preview.duplicate ? (
              <div className="text-xs text-red-400 mt-1" data-testid="roster-preview-dup">
                Already exists — pick different options.
              </div>
            ) : null}
          </div>
        ) : (
          <div className="text-sm text-slate-500">Pick Sport, Level and Year…</div>
        )}
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, setField, entities, allValues, entityKey, orgId, onNavigate }) {
  const v = value ?? '';
  // Own-team identity set (Games→Opponent picker) — DB-derived, replaces the old
  // hardcoded SWS_OWN_TEAM_RX. Only fetched for the opponents-filtered field.
  const [ownIdentity, setOwnIdentity] = useState(null);
  useEffect(() => {
    if (field.optionFilter !== 'opponents' || !orgId) return undefined;
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ol-canonical/own-identity/${orgId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive) setOwnIdentity(new Set(d?.data || [])); })
      .catch(() => {});
    return () => { alive = false; };
  }, [field.optionFilter, orgId]);
  // S1: dynamic role/area options from the ONE staff-role registry (no
  // hardcoded lists in the config). Fetched only for the staff role/area fields.
  const [staffReg, setStaffReg] = useState(null);
  useEffect(() => {
    if (!orgId || !['staff_roles', 'staff_areas'].includes(field.optionsFrom)) return undefined;
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ol-canonical/staff-registry/${orgId}?pickable=1`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive) setStaffReg(d?.data || { roles: [], areas: [] }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [field.optionsFrom, orgId]);
  const [slugOverride, setSlugOverride] = useState(false);

  if (field.type === 'staff_assignments') {
    return <StaffAssignmentsEditor orgId={orgId} coachId={allValues?.id} />;
  }

  if (field.type === 'slug') {
    const isEdit = allValues?.id;
    const autoSlug = generateSlug(entityKey, allValues || {}, entities);
    const displaySlug = field.noOverride
      ? (isEdit ? (v || autoSlug) : autoSlug)
      : (slugOverride ? v : (v || autoSlug));

    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-emerald-400 font-mono truncate">
            {displaySlug || '...'}
          </code>
          {!field.noOverride && !isEdit && (
            <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={slugOverride} onChange={e => { setSlugOverride(e.target.checked); if (!e.target.checked) onChange(autoSlug); }} className="rounded h-3 w-3" />
              Override
            </label>
          )}
        </div>
        {!field.noOverride && slugOverride && (
          <Input value={v} onChange={e => onChange(sanitizeSlug(e.target.value))} placeholder="Custom slug" className="bg-slate-900 border-slate-700 text-emerald-400 font-mono h-9" />
        )}
        {field.noOverride && (
          <p className="text-[10px] text-slate-500">Auto-generated from the fields below — cannot be edited by hand.</p>
        )}
        {!field.noOverride && !isEdit && !slugOverride && autoSlug && (
          <p className="text-[10px] text-slate-500">Auto-generated per naming conventions. Check "Override" to customize.</p>
        )}
        {!field.noOverride && isEdit && (
          <div className="flex items-center gap-1 text-[10px] text-amber-400">
            <AlertTriangle className="h-3 w-3" />
            Changing a slug breaks existing shortcodes and bookmarks.
          </div>
        )}
      </div>
    );
  }
  if (field.type === 'text') {
    return <Input value={v} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''} className="bg-slate-900 border-slate-700 text-slate-200 h-9" />;
  }
  if (field.type === 'computed_result') {
    // Auto-compute result from our_score and opp_score
    const ours = Number(allValues?.our_score) || 0;
    const opps = Number(allValues?.opp_score) || 0;
    let computed = '';
    if (ours || opps) {
      const prefix = ours > opps ? 'W' : ours < opps ? 'L' : 'T';
      computed = `${prefix}: ${ours}-${opps}`;
    }
    // Auto-update the value if it differs
    if (computed && computed !== v) {
      setTimeout(() => onChange(computed), 0);
    }
    return (
      <div className="flex items-center gap-2">
        <code className={`px-3 py-1.5 rounded text-sm font-mono font-bold ${ours > opps ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : ours < opps ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-slate-700/50 text-slate-300 border border-slate-600'}`}>
          {computed || '—'}
        </code>
        <span className="text-[10px] text-slate-500">Auto-calculated from scores</span>
      </div>
    );
  }
  if (field.type === 'result_text') {
    // Free-text Game Result — mirrors the MSTW "Game Result" field so operators
    // can edit the full string incl. set-score breakdown. Sabres score FIRST.
    return (
      <div className="space-y-1.5">
        <Input
          value={v || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="e.g. W: 3-0 (25-20, 25-18, 25-16)"
          data-testid="game-result-input"
          className="bg-slate-900 border-slate-700 text-slate-200 h-9 font-mono"
        />
        <p className="text-[10px] leading-relaxed text-slate-500">
          If a result is entered here, it replaces the game time in all front-end displays.{' '}
          <span className="text-amber-400 font-medium">Sabres score first</span> (never flipped for home/away).
          Examples — Volleyball: <code className="text-slate-400">W: 2-0 (25-23, 25-16)</code> · Basketball:{' '}
          <code className="text-slate-400">W: 88-72</code> · Baseball: <code className="text-slate-400">W: 5-1</code>.
          Most volleyball matches are best-of-3 (some best-of-5), so up to 5 set scores may go inside the parentheses.
          Leave blank to auto-generate <code className="text-slate-400">W: {'{our}'}-{'{opp}'}</code> from the scores.
        </p>
      </div>
    );
  }
  if (field.type === 'number') {
    return <Input type="number" value={v} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} className="bg-slate-900 border-slate-700 text-slate-200 h-9" />;
  }
  if (field.type === 'textarea') {
    return <textarea value={v} onChange={e => onChange(e.target.value)} rows={3} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-slate-200 text-sm resize-y" />;
  }
  if (field.type === 'datetime') {
    // OL game epochs are "floating-local": the wall-clock is encoded at UTC
    // offset 0. Display/edit the literal digits (read/write at UTC), NEVER
    // converting to the org zone — that keeps the OL UI matching the website.
    // Label shows the org HOME zone so an editor anywhere (e.g. California)
    // knows they enter the org's local time. See /app/docs/OL_TIMEZONE.md §5A.
    const homeTz = (allValues?._siteTimezone && allValues._siteTimezone.includes('/')) ? allValues._siteTimezone : 'America/New_York';
    const pad = (n) => String(n).padStart(2, '0');
    let dtStr = '';
    if (v) {
      const d = new Date(v * 1000);
      dtStr = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
    }
    const handleChange = (localStr) => {
      if (!localStr) { onChange(0); return; }
      const m = String(localStr).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      onChange(m ? Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) / 1000) : 0);
    };
    return (
      <div className="space-y-1">
        <Input type="datetime-local" value={dtStr} onChange={e => handleChange(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200 h-9" />
        <p className="text-[10px] text-slate-500">Time zone: {homeTz} (org home time — entered as local, not your device time)</p>
      </div>
    );
  }
  if (field.type === 'game_datetime') {
    // MSTW splits date and time into two controls. Same floating-local rule as
    // the `datetime` type: encode/decode at UTC offset 0 (literal wall clock),
    // browser-independent. See /app/docs/OL_TIMEZONE.md §5A.
    const homeTz = (allValues?._siteTimezone && allValues._siteTimezone.includes('/')) ? allValues._siteTimezone : 'America/New_York';
    const pad = (n) => String(n).padStart(2, '0');
    let dateStr = '', timeStr = '';
    if (v) {
      const d = new Date(v * 1000);
      dateStr = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
      timeStr = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
    }
    const toUnix = (dstr, tstr) => {
      const dm = String(dstr || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!dm) return 0;
      const tm = String(tstr || '00:00').match(/^(\d{1,2}):(\d{2})/);
      const hh = tm ? +tm[1] : 0, mm = tm ? +tm[2] : 0;
      return Math.floor(Date.UTC(+dm[1], +dm[2] - 1, +dm[3], hh, mm) / 1000);
    };
    const onDate = (d) => {
      if (!d) { onChange(0); return; }
      onChange(toUnix(d, timeStr || '00:00'));
    };
    const onTime = (t) => {
      if (!dateStr) return; // need a date before a time is meaningful
      onChange(toUnix(dateStr, t || '00:00'));
    };
    return (
      <div className="space-y-2" data-testid="game-datetime">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-slate-200 text-xs">Date</Label>
            <Input type="date" value={dateStr} onChange={e => onDate(e.target.value)}
              className="ol-dark-picker bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid="game-date-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-200 text-xs">Game Time (Minutes)</Label>
            <Input type="time" value={timeStr} onChange={e => onTime(e.target.value)}
              className="ol-dark-picker bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid="game-time-input" />
          </div>
        </div>
        <p className="text-[10px] text-slate-500">Time zone: {homeTz} (org home time — entered as local, not your device time). Use Time TBA below when the start time is undecided.</p>
      </div>
    );
  }
  if (field.type === 'toggle') {
    const on = field.onLabel || 'Yes';
    const off = field.offLabel || 'No';
    return (
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => onChange(v ? 0 : 1)} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${v ? 'bg-amber-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
          {v ? on : off}
        </button>
      </div>
    );
  }
  if (field.type === 'select') {
    let opts = field.options || [];
    // Dynamic options from the org's active sports (SOT) — no hardcoded list.
    // `optionsFrom: 'sports'` yields an "All sports" entry + one per sport code.
    if (field.optionsFrom === 'sports') {
      const seen = new Set();
      const dyn = [];
      for (const s of (entities?.sports || [])) {
        const code = String(s.slug || '').toLowerCase();
        if (!code || seen.has(code)) continue;
        seen.add(code);
        dyn.push({ v: code, l: s.name || s.title || code });
      }
      opts = [{ v: '', l: 'All sports' }, ...dyn];
    }
    // S1: staff roles from the registry — scope-filtered by whether a Sport is
    // chosen (team) or left as Organization (org). Static list is gone.
    if (field.optionsFrom === 'staff_roles') {
      const wantScope = (allValues?.sport ? 'team' : 'org');
      const roles = (staffReg?.roles || []).filter(r => (r.scope || 'team') === wantScope);
      opts = roles.map(r => ({ v: r.code, l: r.label }));
      if (!opts.length) opts = [{ v: '', l: 'Loading roles…' }];
    }
    // S1/S2: org areas from the registry (shown only for org-scope positions).
    if (field.optionsFrom === 'staff_areas') {
      opts = (staffReg?.areas || []).map(a => ({ v: a.code, l: a.label }));
      if (!opts.length) opts = [{ v: '', l: 'Loading areas…' }];
    }
    return (
      <Select value={String(v || opts[0]?.v || '__none__')} onValueChange={nv => onChange(nv === '__none__' ? '' : nv)}>
        <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-9"><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map(o => <SelectItem key={o.v || '__none__'} value={o.v || '__none__'}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (field.type === 'roster_composer') {
    return <RosterComposer orgId={orgId} setField={setField} />;
  }
  if (field.type === 'roster_picker') {
    return <RosterPicker entities={entities} v={v} onChange={onChange} onNavigate={onNavigate} allValues={allValues} />;
  }
  if (field.type === 'season_roster') {
    return <SeasonRosterSelect entities={entities} v={v} onChange={onChange} allValues={allValues} />;
  }
  if (field.type === 'entity') {
    // Entity picker — dropdown of loaded entities (e.g. Opponent → teams,
    // Game Location → venues). The source list can carry duplicate-named
    // rows (e.g. two "Alamance" opponent records, the SWS teams stored
    // twice); collapse them by display label so the dropdown is clean, but
    // always keep the row that matches the current value so an existing
    // selection still resolves. AdminManager supplies the COMPLETE teams /
    // venues lists here (not the 200-row paginated grid data) so every
    // opponent/venue is selectable — no more truncation at "G".
    const entityList = entities?.[field.entity] || [];
    const titleField = field.entity === 'rosters' || field.entity === 'seasons' || field.entity === 'leagues' ? 'name' : 'title';
    const labelOf = (e) => e[titleField] || e.title || e.name || e.slug || String(e.id);
    // `optionFilter: 'opponents'` → hide the Sabres' own teams + event rows,
    // but never hide the row that is currently selected (so an existing game
    // that references a now-filtered team still displays its value).
    // Value key: some pickers key on a stable slug rather than the numeric team
    // id. Games → Opponent uses `opponent_team_slug` (the field the games
    // collection, WP sync and opponent enrichment all actually read), so an
    // existing game resolves and there is a single source of truth for opponent.
    const valOf = (e) => (field.optionValue ? (e[field.optionValue] ?? '') : e.id);
    const sourceList = field.optionFilter === 'opponents'
      ? entityList.filter(e => isRealOpponent(e, labelOf, ownIdentity) || String(valOf(e)) === String(v))
      : (field.optionFilterFn
          ? entityList.filter(e => field.optionFilterFn(e, labelOf, allValues) || String(valOf(e)) === String(v))
          : entityList);
    const byLabel = new Map();
    for (const e of sourceList) {
      if (String(valOf(e) ?? '') === '') continue; // no usable value → not selectable
      const key = (labelOf(e) || '').trim().toLowerCase() || `id:${e.id}`;
      const existing = byLabel.get(key);
      if (!existing) { byLabel.set(key, e); continue; }
      if (String(valOf(e)) === String(v)) byLabel.set(key, e); // prefer current selection
    }
    let options = [...byLabel.values()].sort((a, b) => labelOf(a).localeCompare(labelOf(b)));
    // If the stored value isn't a known option (e.g. a historical game whose
    // opponent/venue slug has no matching Teams/Venues row), still surface its
    // display name so the field never reads a misleading "None".
    if (v && !options.some(e => String(valOf(e)) === String(v))) {
      const lbl = (field.labelFrom && allValues?.[field.labelFrom])
        || (field.syncLabelTo && allValues?.[field.syncLabelTo])
        || String(v).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const vk = field.optionValue || 'id';
      options = [{ id: `__cur__${v}`, [vk]: v, [titleField]: lbl, __synthetic: true }, ...options];
    }
    // `syncLabelTo` → picking a team also writes its display name into another
    // field (Games: Opponent dropdown → `opponent` text), so older text-only
    // games "upgrade" cleanly and the published name stays in sync.
    const handleSelect = (nv) => {
      const val = nv === '__none__' ? '' : nv;
      onChange(val);
      if (field.syncLabelTo && val && setField) {
        const picked = entityList.find(e => String(valOf(e)) === String(val));
        if (picked) setField(field.syncLabelTo, labelOf(picked));
      }
    };
    return (
      <Select value={String(v || '__none__')} onValueChange={handleSelect}>
        <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid={`entity-picker-${field.key}`}><SelectValue placeholder={`Select ${field.label}`} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">None</SelectItem>
          {options.map(e => <SelectItem key={e.id} value={String(valOf(e))}>{labelOf(e)}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === 'entity_multi') {
    // Chip toggles over an entity list (Teams ▸ "Also plays": extra sports for
    // ONE team row). Stores an array of the entity's `optionValue` (ol_id).
    const list = (entities?.[field.entity] || [])
      .filter((e) => !field.excludeKey || String(e[field.optionValue || 'id']) !== String(allValues?.[field.excludeKey] || ''));
    const cur = Array.isArray(v) ? v : [];
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`field-${field.key}`}>
        {list.map((e) => {
          const val = e[field.optionValue || 'id'];
          const on = cur.includes(val);
          return (
            <button key={val} type="button" onClick={() => onChange(on ? cur.filter((x) => x !== val) : [...cur, val])}
              className={`px-2 py-0.5 rounded-full border text-xs ${on ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-600 text-slate-300'}`}>
              {e.title || e.name}
            </button>
          );
        })}
        {!list.length && <span className="text-xs text-slate-500">—</span>}
      </div>
    );
  }
  if (field.type === 'schedule_picker') {
    return <SchedulePicker entities={entities} v={v} onChange={onChange} />;
  }
  if (field.type === 'vocab_select') {
    return <VocabSelect vocab={field.vocab} v={v} onChange={onChange} label={field.label} />;
  }
  if (field.type === 'season_select') {
    return <SeasonSelect orgId={orgId} sportId={allValues?.sport_id} v={v} onChange={onChange} />;
  }
  if (field.type === 'image') {
    const entityTypeMap = { teams: 'team', players: 'player', venues: 'venue', sports: 'sport', leagues: 'league' };
    // Team logos go through the GridFS media SOT (/api/wp-media/upload-classified,
    // category `logo`) keyed by the team OL uuid — never the legacy WP-direct
    // bridge upload. Alt logo gets a `-alt` owner suffix so it computes a
    // distinct slug. See OL_LOGO §1 + MEDIA_LIBRARY.
    const isTeamLogo = entityKey === 'teams';
    // New team (no id yet): key the asset by the typed name, never the shared
    // literal 'team' (every new team's logo used to become `logo-team`).
    const owner = allValues?.ol_id || allValues?.id
      || `new-${(allValues?.title || 'team').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const classifiedOwner = field.key === 'alt_logo_url' ? `${owner}-alt` : owner;
    // Own squad (has a `code`) with no team-level logo → the org logo is used
    // (derived server-side; OL_LOGO §4). Show a hint so the AD knows why the
    // badge is populated even though this field is empty.
    const usingOrgLogo = isTeamLogo && field.key === 'logo_url'
      && !!(allValues?.code || '').trim() && !v;
    return (
      <>
      <ImageUploader
        orgId={orgId}
        currentUrl={v || ''}
        entityType={entityTypeMap[entityKey] || entityKey}
        entityId={allValues?.id}
        field={field.metaField || field.key}
        label={field.label}
        uploadMode={isTeamLogo ? 'classified' : 'bridge'}
        category={isTeamLogo ? 'logo' : undefined}
        owner={isTeamLogo ? classifiedOwner : undefined}
        onUploaded={(result) => onChange(result.url)}
        onRemoved={() => onChange('')}
      />
      {usingOrgLogo && (
        <p className="mt-1 text-xs text-amber-400/90 flex items-center gap-1"
           data-testid="team-logo-using-org-hint"
           title="This is one of the Sabres' own squads. With no team-specific logo set, the organization logo (AD → Profile → Identity) is shown everywhere — the website, the Sabres app and the WordPress scoreboard. Upload a logo here to override it for this squad only.">
          <span aria-hidden="true">ⓘ</span> Using organization logo
        </p>
      )}
      </>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!v}
          onChange={e => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
          data-testid={`field-${field.key}`}
        />
        <span className="text-xs text-slate-300 leading-tight">
          {field.help || 'Enable this option'}
        </span>
      </label>
    );
  }
  if (field.type === 'textarea') {
    return (
      <textarea
        value={v}
        onChange={e => onChange(e.target.value)}
        rows={3}
        className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-200 text-sm focus:outline-none focus:border-amber-500 resize-y"
      />
    );
  }
  return <Input value={v} onChange={e => onChange(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200 h-9" />;
}

// Season picker (Schedules) — lists ONLY the current + next season for the
// chosen sport, from the backend season rule (no hardcoded years). Stores the
// slug year; shows the academic season label.
function SeasonSelect({ orgId, sportId, v, onChange }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!sportId) { setOpts([]); return; }
    bridgeApi.seasonOptions(orgId, sportId).then(r => { if (alive) setOpts(r?.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, [orgId, sportId]);
  return (
    <Select value={v ? String(v) : '__none__'} onValueChange={(nv) => onChange(nv === '__none__' ? '' : nv)}>
      <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid="schedule-season-picker">
        <SelectValue placeholder={sportId ? 'Select Season' : 'Pick a Sport first'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">—</SelectItem>
        {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// Level picker (Teams → own squads). Options come from the BACKEND vocabulary
// (GET /vocab-levels, §1.2) — never a hardcoded frontend list.
function VocabSelect({ vocab, v, onChange }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    let alive = true;
    bridgeApi.vocabLevels().then(r => { if (alive) setOpts(r?.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, [vocab]);
  return (
    <Select value={v || '__none__'} onValueChange={(nv) => onChange(nv === '__none__' ? '' : nv)}>
      <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-9" data-testid="team-level-picker">
        <SelectValue placeholder="Select Level" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">None (opponent)</SelectItem>
        {opts.map(o => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}


// Helpers for dotted-path access (used by `_ol_meta.opponent_type` style fields)
function _get(obj, path) {
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}
function _set(obj, path, value) {
  const keys = path.split('.');
  const out = { ...obj };
  let cur = out;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return out;
}

function FormSection({ section, values, onChange, entities, entityKey, orgId, onNavigate }) {
  const [collapsed, setCollapsed] = useState(!!section.collapsed);

  return (
    <Card className={`bg-slate-800/40 border-slate-700/50 ${section.badge ? 'ring-1 ring-amber-500/20' : ''}`}>
      <button type="button" className="w-full px-4 py-3 flex items-center justify-between text-left" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">{section.title}</h4>
          {section.badge && (
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[9px] uppercase">{section.badge}</Badge>
          )}
        </div>
        {collapsed ? <ChevronRight className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      {!collapsed && (
        <CardContent className="px-4 pb-4 pt-0 space-y-3">
          {section.description && (
            <p className="text-[11px] text-slate-400 leading-relaxed border-l-2 border-amber-500/40 pl-2 italic">{section.description}</p>
          )}
          {section.fields.filter(field => !field.showIf || field.showIf(values)).map(field => (
            <div key={field.key} className="space-y-1">
              {!field.hideLabel && (
                <Label className="text-slate-200 text-xs">{field.label}{field.required && <span className="text-red-400 ml-0.5">*</span>}</Label>
              )}
              <FieldRenderer
                field={field}
                value={field.key.includes('.') ? _get(values, field.key) : values[field.key]}
                onChange={v => onChange(field.key, v)}
                setField={onChange}
                entities={entities}
                allValues={values}
                entityKey={entityKey}
                orgId={orgId}
                onNavigate={onNavigate}
              />
              {field.help && field.type !== 'checkbox' && field.type !== 'result_text' && (
                <p className="text-[10px] text-slate-500 leading-relaxed">{field.help}</p>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

export default function EntityForm({ entityConfig, initialValues, onSave, onDelete, onCancel, saving, entities, entityKey, orgId, onNavigate }) {
  const [values, setValues] = useState({});
  const [missing, setMissing] = useState([]);
  const isEdit = initialValues && initialValues.id;

  // Reset the form only when the record it shows CHANGES. Callers build
  // `initialValues` inline, so it is a new object on every parent redraw — keyed
  // by identity, a redraw (a picker list finishing loading, `saving` toggling, an
  // error) wiped what the user was typing: "Naca Baseball Tournament" saved as
  // " Baseball Tournament" (AJ 2026-10-20, OL-BUG-20261020-05).
  const initKey = JSON.stringify(initialValues || {});
  useEffect(() => {
    setValues(initialValues || {});
    setMissing([]);
  }, [initKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key, val) => {
    setValues(prev => key.includes('.') ? _set(prev, key, val) : ({ ...prev, [key]: val }));
  };

  const handleSubmit = () => {
    // Required fields: say which are empty (a silent no-op looked like a dead button)
    const empty = [];
    for (const section of (entityConfig.formSections || [])) {
      for (const field of section.fields) {
        if (field.required && !String(values[field.key] ?? '').trim()) empty.push(field.label || field.key);
      }
    }
    setMissing(empty);
    if (empty.length) return;
    onSave?.(values);
  };

  const title = isEdit
    ? `Edit ${entityConfig.label}: ${values.title || values.name || values.id || ''}`
    : `New ${entityConfig.label}`;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="entity-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel}><ArrowLeft className="h-5 w-5 text-slate-400" /></Button>
          <h2 className="text-lg font-bold text-slate-100">{title}</h2>
        </div>
        <div className="flex gap-2">
          {isEdit && onDelete && (
            <Button variant="outline" size="sm" onClick={() => onDelete(values)} className="border-red-800 text-red-400 hover:bg-red-900/50" data-testid="delete-entity-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      {(entityConfig.formSections || []).map((section, i) => (
        <FormSection key={i} section={section} values={values} onChange={handleChange} entities={entities} entityKey={entityKey} orgId={orgId} onNavigate={onNavigate} />
      ))}

      {missing.length > 0 && (
        <div className="mx-1 mb-2 rounded border border-red-500/50 bg-red-900/20 text-red-400 text-sm px-3 py-2" data-testid="entity-form-missing">
          Please fill in: {missing.join(', ')}
        </div>
      )}
      <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur border-t border-slate-700 -mx-4 px-4 py-3 flex justify-end gap-2 z-10">
        <Button variant="outline" onClick={onCancel} className="border-slate-600 text-slate-300">Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-entity-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          {isEdit ? 'Save Changes' : `Create ${entityConfig.label}`}
        </Button>
      </div>
    </div>
  );
}
